import { Component, OnInit, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ParentsService } from 'src/app/shared/services/parents.service';
import { StudentsService } from 'src/app/shared/services/students.service';
import { IEmployeeContact } from "src/app/shared/services/parent.services.models";
import { ProfileService } from 'src/app/shared/services/profile.service';
import { ToastrService } from 'ngx-toastr';
import { GoogleWorkspaceConsent } from '../../models/google-workspace-consent';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-google-consent',
  templateUrl: './google-consent.component.html',
  styleUrls: ['./google-consent.component.scss']
})
export class GoogleConsentComponent implements OnInit {
  isLoading: boolean = false;
  attemptedSubmit: boolean = false;
  isStudent: boolean = false;
  isAdmin: boolean = false;
  consentGiven: boolean | null = null;

  studentId: string;
  studentName: string;
  parentId: string;
  parentName: string;
  currentSchoolYear: string = '';
  language: string = 'en';

  consentHistory: GoogleWorkspaceConsent[] = [];
  existingConsent: GoogleWorkspaceConsent | null = null;
  signature: string = '';
  pdfUrl: SafeResourceUrl;

  currentDate: Date = new Date();
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private parentService: ParentsService,
    private studentService: StudentsService,
    private profileService: ProfileService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    const currentUser = this.profileService.identity;
    this.language = this.profileService.profile.language;

    this.isStudent = currentUser.userType?.toLowerCase() === 'student';
    this.isAdmin = currentUser.userType?.toLowerCase() === 'administrator';

    this.studentId = this.route.snapshot.paramMap.get('studentId');
    this.parentName = currentUser.displayName;
    this.parentId = this.parentService.parentId;

    if (this.isAdmin) {
      this.signature = this.parentName;
    }

    this.parentService.loadConsentFormEnabled().subscribe(enabled => {
      if (!enabled) {
        if (this.isAdmin) {
          this.router.navigateByUrl(`/administrator/contacts/profile/${this.studentId}`);
        } else {
          this.router.navigate(['..'], { relativeTo: this.route });
        }
        return;
      }

      this.getStudentName();
      this.getSchoolYear();
      this.loadConsentFormUrl();
      this.loadConsentStatus();
      this.loadConsentHistory();
    });
  }

  onConsentChange(value: boolean) {
    this.consentGiven = value;
  }

  loadConsentStatus() {
    this.isLoading = true;

    this.parentService.getGoogleConsentForm(this.studentId, this.currentSchoolYear).subscribe(
      (response) => {
        this.isLoading = false;

        if (response?.success && response.consentForm) {
          this.existingConsent = response.consentForm;

          this.consentGiven = this.existingConsent.consent === 'Y';

        } else {
          this.existingConsent = null;
          this.consentGiven = null;
        }
      },
      (error) => {
        this.isLoading = false;
        this.existingConsent = null;
        this.consentGiven = null;
      }
    );
  }

  loadConsentHistory(): void {
    this.parentService.getGoogleConsentFormHistory(this.studentId).subscribe(
      (response) => {
        if (response.success && response.consentForm) {
          this.consentHistory = response.consentForm
            .map((item: GoogleWorkspaceConsent) => ({
              ...item,
              consentDate: new Date(item.consentDate + 'Z') // force UTC parsing
            }))
            .sort((a, b) => b.consentDate.getTime() - a.consentDate.getTime());
        }
      }
    );
  }


  onSubmit() {
    this.attemptedSubmit = true;

    if (this.consentGiven === null) {
      this.toastr.error('Please select whether you consent or do not consent.');
      return;
    }

    if (!this.signature || this.signature.trim().length === 0) {
      this.toastr.error('Please provide your signature.');
      return;
    }

    this.isLoading = true;

    const payload = this.createConsentPayload(this.consentGiven ? 'Y' : 'N');

    this.parentService.addGoogleConsentForm(payload).subscribe(
      () => {
        this.isLoading = false;
        this.toastr.success('Thank you for your response.');

        this.loadConsentHistory();

        if (this.isAdmin) {
          this.router.navigateByUrl(`/administrator/contacts`);
        } else {
          this.router.navigate(['..'], { relativeTo: this.route });
        }
      },
      (error) => {
        this.isLoading = false;
        this.toastr.error('Failed to save consent. Please try again.');
      }
    );
  }

  onCancel() {
    if (this.isAdmin) {
      this.router.navigateByUrl(`/administrator/contacts/profile/${this.studentId}`);
    } else {
      this.router.navigate(['..'], { relativeTo: this.route });
    }
  }

  getSchoolYear() {
    const now = new Date();
    const year = now.getFullYear();
    const isBeforeJuly = now.getMonth() < 6;

    const startYear = isBeforeJuly ? year - 1 : year;
    const endYear = startYear + 1;

    this.currentSchoolYear = `${startYear}-${endYear}`;
  }

  getStudentName(): void {
    this.studentService.getStudentById(this.studentId).pipe(
      map(student => `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim())
    ).subscribe(name => {
      this.studentName = name;
    });
  }

  private createConsentPayload(consent: 'Y' | 'N'): any {
    return {
      studentId: this.studentId,
      parentId: this.parentId,
      parentName: this.parentName,
      consent,
      consentDate: new Date(),
      schoolYear: this.currentSchoolYear,
      signature: this.signature?.trim() || undefined
    };
  }

  loadConsentFormUrl(): void {
    const language = this.language || 'en';

    this.parentService.getFeatureConfigurationByName('google_consent_form_url_' + language).subscribe(
      (response: { name: string; value: string }) => {
        if (response && response.value && response.value.trim() !== '') {
          const rawUrl = response.value.trim();
          this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(rawUrl);
        } else {
          this.pdfUrl = null;
        }
      },
      (error) => {
        this.pdfUrl = null;
      }
    );
  }

}
